import { useState, useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import "./App.css";
import CourseApp from "./pages/CourseApp";
import BottomNav from "./components/ui/BottomNav";
import ResumeTrainingButton from "./components/ResumeTrainingButton";
import AdminPanel from "./pages/AdminPanel";
import CourseLogin from "./pages/CourseLogin";
import AccountPage from "./pages/AccountPage";
import SchemaViewer from "./pages/SchemaViewer";
import SchemaDemo from "./pages/SchemaDemo";
import WorkoutLogger from "./pages/WorkoutLogger";
import BlogArticlePage from "./pages/BlogArticlePage";
import WorkoutCustomLogger from "./pages/WorkoutCustomLogger";
import VrijeTrainingLogger from "./pages/VrijeTrainingLogger";
import ProgressDashboard from "./pages/ProgressDashboard";
import CustomSchemaViewer from "./pages/CustomSchemaViewer";
import UserSchemaBuilderPage from "./pages/UserSchemaBuilderPage";
import BestellenPage from "./pages/BestellenPage";
import IntakePage from "./pages/IntakePage";
import PTIntakePage from "./pages/PTIntakePage";
import HyroxViewer from "./pages/HyroxViewer";
import ProgressPage from "./pages/ProgressPage";
import BetalingPage from "./pages/BetalingPage";
import BetalingCreditsPage from "./pages/BetalingCreditsPage";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ThemeProvider, useTheme } from "./context/ThemeContext";
import {
  ChevronDown, Menu, X, BookOpen, Mail, User, Lock,
  Eye, EyeOff, LogIn, LogOut, Shield, Sun, Moon
} from "lucide-react";

// Landing page sections (extracted for maintainability)
import HeroSection from "./components/landing/HeroSection";
import UspBar from "./components/landing/UspBar";
import ProductsSection from "./components/landing/ProductsSection";
import HoeHetWerkt from "./components/landing/HoeHetWerkt";
import WaaromFitSmith from "./components/landing/WaaromFitSmith";
import OverFitSmith from "./components/landing/OverFitSmith";
import ReviewsSection from "./components/landing/ReviewsSection";
import BlogSection from "./components/landing/BlogSection";
import FAQ from "./components/landing/FAQ";
import ContactSection from "./components/landing/ContactSection";
import OverOnsTeaser from "./components/landing/OverOnsTeaser";
import Footer from "./components/landing/Footer";
import AppInstallPage from "./pages/AppInstallPage";
import SetupPasswordPage from "./pages/SetupPasswordPage";
import AlgemeneVoorwaardenPage from "./pages/AlgemeneVoorwaardenPage";
import RestitutiebeleidPage from "./pages/RestitutiebeleidPage";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import CookieBeleidPage from "./pages/CookieBeleidPage";
import OverOnsPage from "./pages/OverOnsPage";
import PwaInstallBanner from "./components/PwaInstallBanner";
import BedrijfAdminPanel from "./pages/BedrijfAdminPanel";
import GroepModus from "./pages/GroepModus";
import PTModus from "./pages/PTModus";
import { useCompanyBranding } from "./hooks/useCompanyBranding";
import TrainerDashboard from "./pages/TrainerDashboard";
import CompanySchemaEditor from "./pages/CompanySchemaEditor";
import CompanyIntakePage from "./pages/CompanyIntakePage";
import MijnSchemaPage from "./pages/MijnSchemaPage";
import PricingSection from "./pages/landing/PricingSection";
import CursusCard from "./components/landing/CursusCard";

// ── PT Kickstart sectie onderaan de pagina ─────────────────────────────────────
function PTKickstartSection() {
  return (
    <section className="py-20 lg:py-28 bg-[#060606] relative overflow-hidden">
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ background: "radial-gradient(ellipse 70% 50% at 50% 0%, rgba(201,168,76,0.05) 0%, transparent 70%)" }}
      />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 relative">
        <div className="text-center mb-10">
          <p className="overline mb-3">Online Cursus</p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4 leading-tight">
            Word een professionele<br />
            <span style={{ color: "#C9A84C" }}>Personal Trainer</span>
          </h2>
          <p className="text-zinc-400 text-base max-w-xl mx-auto">
            Leer in 4 weken alles wat je nodig hebt om sporters te begeleiden — van voeding tot schema's.
          </p>
        </div>
        <CursusCard />
      </div>
    </section>
  );
}
import Registreren from "./pages/Registreren";
import TrainingPage from "./pages/account/TrainingPage";
import VoedingPage from "./pages/account/VoedingPage";
import LichaamPage from "./pages/account/LichaamPage";
import VoortgangPage from "./pages/account/VoortgangPage";
import RoosterPage from "./pages/account/RoosterPage";
import CommunityPage from "./pages/account/CommunityPage";
import ProfielPage from "./pages/account/ProfielPage";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_fitsmith-academy/artifacts/zbh0gz3y_291E0B11-36FD-4420-BAF1-DDEC0FEFC105.png";

// ─── DASHBOARD WRAPPER — past data-theme toe op dashboard routes ──────────────
function DashboardWrapper({ children }) {
  const { user } = useAuth();
  const { theme } = useTheme();
  return (
    <div data-theme={theme} id="dashboard-root" style={{ minHeight: "100vh" }}>
      {children}
    </div>
  );
}

// ─── LOGIN MODAL ──────────────────────────────────────────────────────────────
function LoginModal({ onClose }) {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState("login"); // "login" | "forgot" | "forgot-sent"
  const [resetEmail, setResetEmail] = useState("");
  const [resetLoading, setResetLoading] = useState(false);
  const [resetError, setResetError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) { setError("Vul beide velden in"); return; }
    setLoading(true); setError("");
    try {
      const data = await login(email, password);
      onClose();
      // Redirect based on role
      if (data?.user?.role === "company_admin") {
        window.location.href = "/bedrijf-admin";
      } else if (data?.user?.role === "trainer") {
        window.location.href = "/trainer";
      } else {
        window.location.href = "/account";
      }
    } catch (err) {
      if (err.message && err.message.includes("cloned")) {
        setError("Er is een technisch probleem. Vernieuw de pagina en probeer opnieuw.");
      } else {
        setError(err.message || "Inloggen mislukt");
      }
    } finally { setLoading(false); }
  };

  const handleForgot = async (e) => {
    e.preventDefault();
    if (!resetEmail.trim()) { setResetError("Vul je e-mailadres in"); return; }
    setResetLoading(true); setResetError("");
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: resetEmail.trim() }),
      });
      setView("forgot-sent");
    } catch {
      setResetError("Er ging iets mis. Probeer opnieuw.");
    } finally { setResetLoading(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[100] flex items-center justify-center p-4" onClick={onClose}>
      <div className="w-full max-w-sm bg-[#111] border border-white/10 rounded-2xl p-7 animate-fadeIn" onClick={e => e.stopPropagation()}>

        {/* ── LOGIN ── */}
        {view === "login" && (<>
          <div className="flex items-center justify-between mb-5">
            <div>
              <h2 className="text-white font-bold text-lg">Inloggen</h2>
              <p className="text-zinc-500 text-xs mt-0.5">Toegang tot jouw account</p>
            </div>
            <button onClick={onClose} className="text-zinc-500 hover:text-white transition-colors"><X size={18} /></button>
          </div>
          <form onSubmit={handleSubmit} className="space-y-3">
            <div>
              <label className="course-label">E-mailadres</label>
              <div className="relative">
                <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-600" />
                <input type="email" value={email} onChange={e => { setEmail(e.target.value); setError(""); }}
                  data-testid="modal-login-email" placeholder="jouw@email.com" className="course-input pl-9" autoFocus />
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between" style={{ marginBottom: 5 }}>
                <label className="course-label" style={{ marginBottom: 0 }}>Wachtwoord</label>
                <button type="button" data-testid="forgot-password-link"
                  onClick={() => { setResetEmail(email); setView("forgot"); }}
                  className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 hover:text-[#C9A84C] transition-colors">
                  Vergeten?
                </button>
              </div>
              <div className="relative">
                <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-600" />
                <input type={showPass ? "text" : "password"} value={password}
                  onChange={e => { setPassword(e.target.value); setError(""); }}
                  data-testid="modal-login-code" placeholder="••••••••"
                  className="course-input pl-9 pr-10" />
                <button type="button" onClick={() => setShowPass(!showPass)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300">
                  {showPass ? <EyeOff size={13} /> : <Eye size={13} />}
                </button>
              </div>
            </div>
            {error && (
              <p data-testid="modal-login-error" className="text-red-400 text-sm bg-red-950/30 border border-red-500/20 rounded-lg px-3 py-2">{error}</p>
            )}
            <button type="submit" disabled={loading} data-testid="modal-login-submit"
              className="btn-gold-course w-full py-3 font-bold text-sm uppercase tracking-wide mt-1">
              {loading ? "Bezig..." : "Inloggen"}
            </button>
          </form>
          <p className="text-center text-zinc-600 text-xs mt-4">
            Geen account?{" "}
            <button onClick={() => { onClose(); document.querySelector("#aanbod")?.scrollIntoView({ behavior: "smooth" }); }}
              className="text-[#C9A84C] hover:underline">
              Bekijk het aanbod
            </button>
          </p>
        </>)}

        {/* ── WACHTWOORD VERGETEN ── */}
        {view === "forgot" && (<>
          <div className="flex items-center justify-between mb-5">
            <div>
              <h2 className="text-white font-bold text-lg">Wachtwoord vergeten</h2>
              <p className="text-zinc-500 text-xs mt-0.5">We sturen je een reset-link per e-mail</p>
            </div>
            <button onClick={onClose} className="text-zinc-500 hover:text-white transition-colors"><X size={18} /></button>
          </div>
          <form onSubmit={handleForgot} className="space-y-3">
            <div>
              <label className="course-label">E-mailadres</label>
              <div className="relative">
                <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-600" />
                <input type="email" value={resetEmail} onChange={e => { setResetEmail(e.target.value); setResetError(""); }}
                  data-testid="forgot-email-input" placeholder="jouw@email.com" className="course-input pl-9" autoFocus />
              </div>
            </div>
            {resetError && <p className="text-red-400 text-sm">{resetError}</p>}
            <button type="submit" disabled={resetLoading} data-testid="forgot-submit-btn"
              className="btn-gold-course w-full py-3 font-bold text-sm uppercase tracking-wide">
              {resetLoading ? "Versturen..." : "Reset-link versturen"}
            </button>
          </form>
          <button onClick={() => setView("login")}
            className="w-full text-center text-zinc-600 hover:text-zinc-400 text-xs mt-4 transition-colors">
            ← Terug naar inloggen
          </button>
        </>)}

        {/* ── E-MAIL VERSTUURD ── */}
        {view === "forgot-sent" && (
          <div className="text-center py-2">
            <button onClick={onClose} className="absolute top-5 right-5 text-zinc-500 hover:text-white"><X size={18} /></button>
            <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4"
              style={{ background: "rgba(201,168,76,0.1)", border: "1px solid rgba(201,168,76,0.2)" }}>
              <Mail size={20} style={{ color: "#C9A84C" }} />
            </div>
            <h2 className="text-white font-bold text-lg mb-2">Check je e-mail</h2>
            <p className="text-zinc-400 text-sm leading-relaxed mb-1">
              Als <span className="text-white font-semibold">{resetEmail}</span> bij ons bekend is, ontvang je een reset-link.
            </p>
            <p className="text-zinc-600 text-xs mb-6">Controleer ook je spam-map. De link is 1 uur geldig.</p>
            <button onClick={() => setView("login")} data-testid="back-to-login-btn"
              className="btn-gold-course w-full py-3 font-bold text-sm uppercase tracking-wide">
              Terug naar inloggen
            </button>
          </div>
        )}

      </div>
    </div>
  );
}

// ─── USER DROPDOWN ─────────────────────────────────────────────────────────────
function UserDropdown({ user, onClose }) {
  const { logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const initials = user?.name?.split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase() || "?";
  const isAdmin = user?.role === "admin";
  const go = (path) => { onClose(); navigate(path); };
  const isLight = theme === "light";

  return (
    <div className="absolute right-0 top-full mt-2 w-56 bg-[#111] border border-white/10 rounded-xl shadow-2xl overflow-hidden z-50 animate-fadeIn">
      <div className="px-4 py-3.5 border-b border-white/5">
        <div className="flex items-center gap-2 mb-0.5">
          <p className="text-white font-semibold text-sm">{user.name}</p>
          {isAdmin && <span className="text-xs bg-[#C9A84C]/15 text-[#C9A84C] border border-[#C9A84C]/30 px-1.5 py-0.5 rounded font-bold">Admin</span>}
        </div>
        <p className="text-zinc-500 text-xs truncate">{user.email}</p>
      </div>
      <div className="py-1.5">
        <button onClick={() => go("/account")} data-testid="dropdown-mijn-account" className="dropdown-item"><User size={14} /> Mijn account</button>
        <button onClick={() => go("/cursus")} data-testid="dropdown-mijn-cursus" className="dropdown-item"><BookOpen size={14} /> Mijn cursus</button>
        {isAdmin && (
          <button onClick={() => go("/admin")} data-testid="dropdown-admin-paneel" className="dropdown-item text-[#C9A84C] hover:text-[#C9A84C]">
            <Shield size={14} /> Admin paneel
          </button>
        )}
      </div>
      {/* Dark / Light toggle — alleen zichtbaar als ingelogd */}
      <div className="border-t border-white/5 px-4 py-3">
        <p className="text-zinc-600 text-[10px] uppercase tracking-widest font-bold mb-2">Weergave</p>
        <button
          onClick={toggleTheme}
          data-testid="theme-toggle-btn"
          className={`w-full flex items-center justify-between rounded-xl px-3 py-2.5 transition-all border ${
            isLight
              ? "bg-amber-50/10 border-amber-400/30 text-amber-400"
              : "bg-blue-500/8 border-blue-400/20 text-blue-300"
          }`}
        >
          <div className="flex items-center gap-2.5 text-xs font-semibold">
            {isLight ? <Sun size={13} /> : <Moon size={13} />}
            {isLight ? "Lichte weergave" : "Donkere weergave"}
          </div>
          {/* Toggle pill */}
          <div className={`relative w-9 h-5 rounded-full transition-all ${isLight ? "bg-amber-400" : "bg-zinc-700"}`}>
            <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-all ${isLight ? "left-[18px]" : "left-0.5"}`} />
          </div>
        </button>
      </div>
      <div className="border-t border-white/5 py-1.5">
        <button onClick={() => { onClose(); logout(); }} data-testid="dropdown-uitloggen" className="dropdown-item text-zinc-500 hover:text-red-400">
          <LogOut size={14} /> Uitloggen
        </button>
      </div>
    </div>
  );
}

// ─── NAVBAR ──────────────────────────────────────────────────────────────────
function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);
  const initials = user?.name?.split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase() || "?";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const onClick = (e) => { if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setShowDropdown(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const navLinks = [
    { label: "Home", href: "#hero" }, { label: "Aanbod", href: "#aanbod" },
    { label: "Over FitSmith", href: "#over" }, { label: "Reviews", href: "#reviews" },
    { label: "Contact", href: "#contact" },
  ];

  const scrollTo = (href) => {
    setMobileOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <>
      <nav data-testid="navbar"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? "bg-[#0a0a0a]/95 backdrop-blur-xl shadow-xl border-b border-white/5" : "bg-transparent"}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 lg:h-20">
            <button onClick={() => scrollTo("#hero")} data-testid="nav-logo" className="flex items-center gap-2">
              <img src={LOGO_URL} alt="FitSmith" className="h-10 w-10 object-contain" />
              <span className="text-white font-bold text-xl tracking-wider font-heading">FitSmith</span>
            </button>
            <div className="hidden md:flex items-center gap-8">
              {navLinks.map(link => (
                <button key={link.label} onClick={() => scrollTo(link.href)}
                  data-testid={`nav-link-${link.label.toLowerCase()}`}
                  className="text-zinc-300 hover:text-[#C9A84C] text-sm font-medium transition-colors duration-200">
                  {link.label}
                </button>
              ))}
            </div>
            <div className="hidden md:flex items-center gap-3">
              {user ? (
                <div className="relative" ref={dropdownRef}>
                  <button onClick={() => setShowDropdown(!showDropdown)} data-testid="user-avatar-btn"
                    className="flex items-center gap-2.5 bg-white/5 hover:bg-white/8 border border-white/10 hover:border-[#C9A84C]/30 rounded-full pl-2 pr-3 py-1.5 transition-all">
                    <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#C9A84C] to-[#D4AF37] flex items-center justify-center text-black text-xs font-bold">{initials}</div>
                    <span className="text-white text-sm font-medium">{user.name.split(" ")[0]}</span>
                    <ChevronDown size={13} className={`text-zinc-400 transition-transform ${showDropdown ? "rotate-180" : ""}`} />
                  </button>
                  {showDropdown && <UserDropdown user={user} onClose={() => setShowDropdown(false)} />}
                </div>
              ) : (
                <>
                  <button onClick={() => navigate("/login")} data-testid="nav-bedrijfs-login-btn"
                    className="text-zinc-400 hover:text-[#C9A84C] text-sm font-medium transition-colors duration-200 flex items-center gap-1.5">
                    <Shield size={14} /> Bedrijfs login
                  </button>
                  <button onClick={() => setShowLoginModal(true)} data-testid="nav-login-btn"
                    className="btn-outline px-4 py-2.5 text-sm font-bold flex items-center gap-1.5">
                    <LogIn size={14} /> Inloggen
                  </button>
                  <button onClick={() => scrollTo("#aanbod")} data-testid="nav-cta-btn"
                    className="btn-gold px-5 py-2.5 text-sm font-bold uppercase tracking-wide">
                    Bekijk aanbod
                  </button>
                </>
              )}
            </div>
            <button className="md:hidden text-white p-2" onClick={() => setMobileOpen(!mobileOpen)} data-testid="mobile-menu-toggle">
              {mobileOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
        {mobileOpen && (
          <div className="md:hidden bg-[#0f0f0f] border-t border-white/10 px-4 py-6 space-y-4 animate-fadeIn">
            {user && (
              <div className="flex items-center gap-3 pb-3 border-b border-white/10">
                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#C9A84C] to-[#D4AF37] flex items-center justify-center text-black text-sm font-bold">{initials}</div>
                <div><p className="text-white font-semibold text-sm">{user.name}</p><p className="text-zinc-500 text-xs">{user.email}</p></div>
              </div>
            )}
            {navLinks.map(link => (
              <button key={link.label} onClick={() => scrollTo(link.href)}
                className="block w-full text-left text-white font-medium py-2 border-b border-white/5">{link.label}</button>
            ))}
            {user ? (
              <>
                <button onClick={() => { setMobileOpen(false); navigate("/account"); }}
                  className="block w-full text-left text-zinc-300 py-2 flex items-center gap-2"><User size={14} /> Mijn account</button>
                <button onClick={() => { setMobileOpen(false); navigate("/cursus"); }}
                  className="block w-full text-left text-zinc-300 py-2 flex items-center gap-2"><BookOpen size={14} /> Mijn cursus</button>
                <button onClick={() => { setMobileOpen(false); logout(); }}
                  className="block w-full text-left text-zinc-500 py-2 flex items-center gap-2"><LogOut size={14} /> Uitloggen</button>
              </>
            ) : (
              <>
                <button onClick={() => { setMobileOpen(false); navigate("/login"); }}
                  data-testid="mobile-bedrijfs-login-btn"
                  className="w-full py-3 text-sm font-medium text-zinc-400 hover:text-[#C9A84C] transition-colors flex items-center justify-center gap-2 border border-white/10 rounded-xl">
                  <Shield size={14} /> Bedrijfs login
                </button>
                <button onClick={() => { setMobileOpen(false); setShowLoginModal(true); }}
                  className="btn-outline w-full py-3 text-sm font-bold flex items-center justify-center gap-2"><LogIn size={14} /> Inloggen</button>
                <button onClick={() => scrollTo("#aanbod")}
                  className="btn-gold w-full py-3 text-sm font-bold uppercase tracking-wide mt-2">Bekijk aanbod</button>
              </>
            )}
          </div>
        )}
      </nav>
      {showLoginModal && <LoginModal onClose={() => setShowLoginModal(false)} />}
    </>
  );
}

// ─── LANDING PAGE ─────────────────────────────────────────────────────────────
function HomePage() {
  return (
    <>
      <Navbar />
      <main>
        <HeroSection />
        <UspBar />
        <ProductsSection />
        <PricingSection />
        <HoeHetWerkt />
        <WaaromFitSmith />
        <OverFitSmith />
        <ReviewsSection />
        <BlogSection />
        <OverOnsTeaser />
        <PTKickstartSection />
        <FAQ />
        <ContactSection />
      </main>
      <Footer />
    </>
  );
}

// ─── PWA ENTRY GUARD ──────────────────────────────────────────────────────────
// Redirect alleen bij de EERSTE opening via het PWA-icoon (sessionStorage-vlag).
// Daarna kan de gebruiker vrij naar de homepage navigeren.
function PwaEntryGuard({ children }) {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  // isPwaLaunch = true alleen bij het eerste openen via PWA-icoon
  // sessionStorage zorgt dat terugnavigeren naar "/" niet opnieuw redirect
  const [isPwaLaunch] = useState(() => {
    if (sessionStorage.getItem("pwa_redirected")) return false; // al eerder geredirect
    const params = new URLSearchParams(window.location.search);
    return params.get("source") === "pwa" || window.navigator.standalone === true;
  });

  useEffect(() => {
    if (!isPwaLaunch) return;
    if (loading) return;
    sessionStorage.setItem("pwa_redirected", "1"); // volgende navigatie naar "/" vrij
    navigate(user ? "/account" : "/login", { replace: true });
  }, [isPwaLaunch, loading, user, navigate]);

  // Eerste PWA-opening: toon zwart scherm tijdens auth-check (geen flicker)
  if (isPwaLaunch && loading) {
    return (
      <div className="min-h-screen bg-[#080808] flex flex-col items-center justify-center gap-4">
        <img src={LOGO_URL} alt="FitSmith" className="w-16 h-16 object-contain opacity-80" />
        <div className="w-6 h-6 border-2 border-[#C9A84C]/30 border-t-[#C9A84C] rounded-full animate-spin" />
      </div>
    );
  }

  if (isPwaLaunch && !loading) return null; // redirect onderweg

  // Normale browser of terugnavigeren → altijd homepage tonen
  return children;
}

// ─── PROTECTED ROUTES ─────────────────────────────────────────────────────────
function ProtectedAccount({ children }) {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  useEffect(() => {
    if (!loading && !user) {
      const params = new URLSearchParams(window.location.search);
      const isPwa = params.get("source") === "pwa" || window.navigator.standalone === true;
      navigate(isPwa ? "/login" : "/", { replace: true });
    }
  }, [user, loading, navigate]);
  if (loading) return <div className="min-h-screen bg-[#080808] flex items-center justify-center"><div className="text-zinc-500">Laden...</div></div>;
  return user ? (children || <AccountPage />) : null;
}

function ProtectedCourse() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  useEffect(() => {
    if (!loading && !user) navigate("/login");
    if (!loading && user && user.plan !== "full") navigate("/account");
  }, [user, loading, navigate]);
  if (loading) return <div className="min-h-screen bg-[#080808] flex items-center justify-center"><div className="text-zinc-500">Laden...</div></div>;
  return user && user.plan === "full" ? (
    <div className="bg-[#080808]" style={{ paddingBottom: "72px" }}>
      <CourseApp />
      <BottomNav />
    </div>
  ) : null;
}

function ProtectedAdmin() {
  // AdminPanel beheert zijn eigen authenticatie via AdminLogin
  return <AdminPanel />;
}

function ProtectedBedrijfAdmin({ children }) {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  useEffect(() => {
    if (!loading && !user) navigate("/login");
    if (!loading && user && user.role !== "company_admin") navigate("/account");
  }, [user, loading, navigate]);
  if (loading) return <div className="min-h-screen bg-[#080808] flex items-center justify-center"><div className="text-zinc-500">Laden...</div></div>;
  if (user?.role !== "company_admin") return null;
  return children ? children : <BedrijfAdminPanel />;
}

function ProtectedTrainer() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  useEffect(() => {
    if (!loading && !user) navigate("/login");
    if (!loading && user && user.role !== "trainer") navigate("/account");
  }, [user, loading, navigate]);
  if (loading) return <div className="min-h-screen bg-[#080808] flex items-center justify-center"><div className="text-zinc-500">Laden...</div></div>;
  return user?.role === "trainer" ? <TrainerDashboard /> : null;
}

function ProtectedSchema() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  useEffect(() => { if (!loading && !user) navigate("/"); }, [user, loading, navigate]);
  if (loading) return null;
  return user ? <SchemaViewer /> : null;
}

function ProtectedHyrox() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  useEffect(() => { if (!loading && !user) navigate("/"); }, [user, loading, navigate]);
  if (loading) return null;
  return user ? <HyroxViewer /> : null;
}

// ─── APP ──────────────────────────────────────────────────────────────────────
function AppWithTheme() {
  const { user } = useAuth();
  return (
    <ThemeProvider userId={user?.id}>
      <BrowserRouter>
        <Routes>
          {/* ── Publieke routes — GEEN thema wrapper ── */}
          <Route path="/" element={<PwaEntryGuard><HomePage /></PwaEntryGuard>} />
          <Route path="/login" element={<CourseLogin />} />
          <Route path="/bestellen" element={<BestellenPage />} />
          <Route path="/registreren" element={<Registreren />} />
          <Route path="/blog/:slug" element={<BlogArticlePage />} />
          <Route path="/restitutiebeleid" element={<RestitutiebeleidPage />} />
          <Route path="/algemene-voorwaarden" element={<AlgemeneVoorwaardenPage />} />
          <Route path="/privacybeleid" element={<PrivacyPolicyPage />} />
          <Route path="/cookiebeleid" element={<CookieBeleidPage />} />
          <Route path="/over-ons" element={<OverOnsPage />} />
          <Route path="/company-intake/:token" element={<CompanyIntakePage />} />
          <Route path="/pt-intake/:orderId" element={<PTIntakePage />} />
          <Route path="/wachtwoord-instellen/:token" element={<SetupPasswordPage />} />

          {/* ── Dashboard routes — MET thema wrapper ── */}
          <Route path="/account" element={<DashboardWrapper><ProtectedAccount /></DashboardWrapper>} />
          <Route path="/account/training"  element={<DashboardWrapper><ProtectedAccount><TrainingPage /></ProtectedAccount></DashboardWrapper>} />
          <Route path="/account/voeding"   element={<DashboardWrapper><ProtectedAccount><VoedingPage /></ProtectedAccount></DashboardWrapper>} />
          <Route path="/account/lichaam"   element={<DashboardWrapper><ProtectedAccount><LichaamPage /></ProtectedAccount></DashboardWrapper>} />
          <Route path="/account/voortgang" element={<DashboardWrapper><ProtectedAccount><VoortgangPage /></ProtectedAccount></DashboardWrapper>} />
          <Route path="/account/rooster"    element={<DashboardWrapper><ProtectedAccount><RoosterPage /></ProtectedAccount></DashboardWrapper>} />
          <Route path="/account/community"  element={<DashboardWrapper><ProtectedAccount><CommunityPage /></ProtectedAccount></DashboardWrapper>} />
          <Route path="/account/profiel"   element={<DashboardWrapper><ProtectedAccount><ProfielPage /></ProtectedAccount></DashboardWrapper>} />
          <Route path="/mijn-schema" element={<DashboardWrapper><MijnSchemaPage /></DashboardWrapper>} />
          <Route path="/cursus/*" element={<DashboardWrapper><ProtectedCourse /></DashboardWrapper>} />
          <Route path="/admin" element={<DashboardWrapper><ProtectedAdmin /></DashboardWrapper>} />
          <Route path="/bedrijf-admin" element={<DashboardWrapper><ProtectedBedrijfAdmin /></DashboardWrapper>} />
          <Route path="/bedrijf-schema-editor" element={<DashboardWrapper><ProtectedBedrijfAdmin><CompanySchemaEditor /></ProtectedBedrijfAdmin></DashboardWrapper>} />
          <Route path="/bedrijf-schema-editor/:schemaId" element={<DashboardWrapper><ProtectedBedrijfAdmin><CompanySchemaEditor /></ProtectedBedrijfAdmin></DashboardWrapper>} />
          <Route path="/trainer" element={<DashboardWrapper><ProtectedTrainer /></DashboardWrapper>} />
          <Route path="/schema/:key" element={<DashboardWrapper><ProtectedSchema /></DashboardWrapper>} />
          <Route path="/schema-demo" element={<DashboardWrapper><SchemaDemo /></DashboardWrapper>} />
          <Route path="/workout/:comboKey/:sessionId" element={<DashboardWrapper><WorkoutLogger /></DashboardWrapper>} />
          <Route path="/custom-schema/:id" element={<DashboardWrapper><CustomSchemaViewer /></DashboardWrapper>} />
          <Route path="/workout-custom/:schemaId/:dagIndex" element={<DashboardWrapper><WorkoutCustomLogger /></DashboardWrapper>} />
          <Route path="/vrije-training" element={<DashboardWrapper><VrijeTrainingLogger /></DashboardWrapper>} />
          <Route path="/progress" element={<ProgressDashboard />} />
          <Route path="/intake/:orderId" element={<DashboardWrapper><IntakePage /></DashboardWrapper>} />
          <Route path="/hyrox/:key" element={<DashboardWrapper><ProtectedHyrox /></DashboardWrapper>} />
          <Route path="/progress" element={<DashboardWrapper><ProgressPage /></DashboardWrapper>} />
          <Route path="/betaling" element={<DashboardWrapper><BetalingPage /></DashboardWrapper>} />
          <Route path="/betaling-credits" element={<BetalingCreditsPage />} />
          <Route path="/app-installeren" element={<AppInstallPage />} />
          <Route path="/schema-maker" element={<DashboardWrapper><UserSchemaBuilderPage /></DashboardWrapper>} />
          <Route path="/schema-maker/:schemaId" element={<DashboardWrapper><UserSchemaBuilderPage /></DashboardWrapper>} />
          <Route path="/groep" element={<DashboardWrapper><GroepModus /></DashboardWrapper>} />
          <Route path="/pt" element={<DashboardWrapper><PTModus /></DashboardWrapper>} />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <PwaInstallBanner />
        <ResumeTrainingButton />
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <AppWithTheme />
    </AuthProvider>
  );
}
